import { authenticatedFetch } from "./authenticatedFetch";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** Works for manual + template documents (backend resolves by job_id). */
export const SIGNATURE_FIELDS_BASE_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_SIGNERS_BASE_PATH || "/documents/manual";

/**
 * @param {unknown} data
 * @returns {{ manual_signers: object[]; signature_anchors: object[] }}
 */
export function normalizeSignatureFieldsResponse(data) {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return {
      manual_signers: Array.isArray(data.manual_signers) ? data.manual_signers : [],
      signature_anchors: Array.isArray(data.signature_anchors) ? data.signature_anchors : [],
    };
  }
  return { manual_signers: [], signature_anchors: [] };
}

/**
 * @param {object | object[] | null | undefined} payload
 * @returns {{ manual_signers: object[]; signature_anchors: object[] }}
 */
export function normalizeSignatureFieldsSavePayload(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    ("signature_anchors" in payload || "manual_signers" in payload)
  ) {
    return {
      manual_signers: Array.isArray(payload.manual_signers) ? payload.manual_signers : [],
      signature_anchors: Array.isArray(payload.signature_anchors)
        ? payload.signature_anchors
        : [],
    };
  }

  if (Array.isArray(payload)) {
    return { manual_signers: payload, signature_anchors: [] };
  }

  return { manual_signers: [], signature_anchors: [] };
}

/**
 * GET /documents/manual/{job_id}/signers — manual and template documents.
 *
 * @param {string} jobId
 * @returns {Promise<{ ok: true; data: { manual_signers: object[]; signature_anchors: object[] } } | { ok: false; message: string }>}
 */
export async function fetchSignatureFields(jobId) {
  if (!jobId) return { ok: false, message: "job_id inválido." };

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${SIGNATURE_FIELDS_BASE_PATH}/${encodeURIComponent(String(jobId))}/signers`,
      { method: "GET" }
    );

    if (!response.ok) {
      let message = "Não foi possível carregar os campos de assinatura.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const data = await response.json();
    return { ok: true, data: normalizeSignatureFieldsResponse(data) };
  } catch (error) {
    console.error("fetchSignatureFields:", error);
    return { ok: false, message: "Erro de rede ao carregar os campos de assinatura." };
  }
}

/**
 * POST /documents/manual/{job_id}/signers — always send manual_signers + signature_anchors together.
 *
 * @param {string} jobId
 * @param {{ manual_signers?: object[]; signature_anchors?: object[] } | object[]} payload
 */
export async function saveSignatureFields(jobId, payload) {
  if (!jobId) return { ok: false, message: "job_id inválido." };

  const body = normalizeSignatureFieldsSavePayload(payload);

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${SIGNATURE_FIELDS_BASE_PATH}/${encodeURIComponent(String(jobId))}/signers`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      let message = "Não foi possível salvar os campos de assinatura.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    return { ok: true };
  } catch (error) {
    console.error("saveSignatureFields:", error);
    return { ok: false, message: "Erro de rede ao salvar os campos de assinatura." };
  }
}
